import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState(['all']);
  const [imageErrors, setImageErrors] = useState(new Set());
  const { user } = useAuth();
  
  // Check if user has admin role
  const isAdmin = user && user.authorities && 
    user.authorities.some(auth => auth.authority === 'ROLE_ADMIN');

  // Product image mapping - you can expand this based on your product data
  const productImages = {
    // Electronics category
    'headphones': '/images/headphones-white.jpg',
    'monitor': '/images/monitor-curved.jpg',
    'curved monitor': '/images/monitor-curved.jpg',
    'wireless headphones': '/images/headphones-blue.jpg',
    'bluetooth headphones': '/images/headphones-blue.jpg',
    'gaming headphones': '/images/headphones-blue.jpg',
    'computer monitor': '/images/monitor-standard.jpg',
    'display': '/images/monitor-curved.jpg',
    'screen': '/images/monitor-standard.jpg',
    'gaming monitor': '/images/monitor-curved.jpg',
    'office monitor': '/images/monitor-standard.jpg',
    // Add more mappings as needed
    'default': '/images/headphones-white.jpg' // fallback image
  };

  useEffect(() => {
    loadProducts();
    if (isAdmin) {
      loadInsights();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterProducts();
  }, [products, selectedCategory, searchTerm]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.getAllProducts();
      console.log('Products loaded from inventory:', data);
      
      // Ensure we have an array
      const productsArray = Array.isArray(data) ? data : [];
      setProducts(productsArray);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(productsArray.map(p => p.category).filter(Boolean))];
      setCategories(['all', ...uniqueCategories]);
      
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(`Failed to load products: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => 
        product.category && product.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(search) ||
        product.description?.toLowerCase().includes(search) ||
        product.sku?.toLowerCase().includes(search)
      );
    }

    setFilteredProducts(filtered);
  };

  const loadInsights = async () => {
    try {
      const data = await apiService.getProductInsights();
      setInsights(data);
    } catch (err) {
      console.warn('Failed to load insights:', err);
    }
  };

  const getStockStatusClass = (product) => {
    if (!product.quantity || product.quantity <= 0) return 'out-of-stock';
    if (product.quantity <= 10) return 'low-stock';
    return 'in-stock';
  };

  const getStockStatusText = (product) => {
    if (!product.quantity || product.quantity <= 0) return 'Out of Stock';
    if (product.quantity <= 10) return 'Low Stock';
    return 'In Stock';
  };

  // Function to get the appropriate image for a product
  const getProductImage = (product) => {
    const productName = product.name?.toLowerCase() || '';
    
    // Check for specific product name matches
    for (const [key, imagePath] of Object.entries(productImages)) {
      if (key !== 'default' && productName.includes(key)) {
        return imagePath;
      }
    }
    
    // Return default image if no match found
    return productImages.default;
  };

  const handleImageError = (productId) => {
    setImageErrors(prev => new Set([...prev, productId]));
  };

  if (loading) {
    return (
      <div className="products-container">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="products-container">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadProducts} className="retry-button">
            Retry
          </button>
        </div>
      )}

      {/* Updated search controls with centered layout */}
      <div className="orders-header-section">
        <div className="orders-title-section">
          <div className="filter-section">
            <label htmlFor="category-filter">Filter by Category:</label>
            <select 
              id="category-filter"
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="search-container-centered">
          <input
            type="text"
            placeholder="Search products by name, SKU, description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-centered"
          />
        </div>
        
        <div className="pagination-info-section">
          <div className="results-info">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>
      </div>

      {isAdmin && insights && (
        <div className="insights-section">
          <h3>Admin Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <h4>Total Products</h4>
              <span className="insight-number">{insights.totalProducts || products.length}</span>
            </div>
            <div className="insight-card">
              <h4>Categories</h4>
              <span className="insight-number">{categories.length - 1}</span>
            </div>
            <div className="insight-card">
              <h4>In Stock</h4>
              <span className="insight-number">{insights.inStockProducts || products.filter(p => p.quantity > 0).length}</span>
            </div>
            <div className="insight-card warning">
              <h4>Low Stock</h4>
              <span className="insight-number">{insights.lowStockProducts || products.filter(p => p.quantity <= 10 && p.quantity > 0).length}</span>
            </div>
          </div>
        </div>
      )}

      <div className="products-content">
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <h3>No products found</h3>
            <p>Try adjusting your search or filter criteria</p>
            {products.length === 0 && (
              <button onClick={loadProducts} className="retry-button">
                Reload Products
              </button>
            )}
          </div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <div key={product.id} className="product-card">
                <div className="product-image">
                  {!imageErrors.has(product.id) ? (
                    <img 
                      src={getProductImage(product)}
                      alt={product.name}
                      onError={() => handleImageError(product.id)}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px 8px 0 0'
                      }}
                    />
                  ) : (
                    <div className="product-placeholder">
                      <span className="product-icon">📦</span>
                    </div>
                  )}
                  <div className={`stock-badge ${getStockStatusClass(product)}`}>
                    {getStockStatusText(product)}
                  </div>
                </div>

                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  
                  <div className="product-meta">
                    <span className="product-category">{product.category}</span>
                    <span className="product-sku">SKU: {product.sku}</span>
                  </div>

                  <div className="product-details">
                    <div className="product-price">${product.price?.toFixed(2) || '0.00'}</div>
                    <div className="product-stock">
                      Stock: {product.quantity || 0} units
                    </div>
                  </div>

                  <div className="product-actions">
                    <Link 
                      to={`/products/${product.id}`} 
                      className="btn btn-primary"
                    >
                      View Details
                    </Link>
                    <button 
                      className="btn btn-secondary"
                      disabled={!product.quantity || product.quantity <= 0}
                    >
                      {product.quantity > 0 ? 'Add to Cart' : 'Out of Stock'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {filteredProducts.length > 0 && (
        <div className="products-summary">
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Products:</span>
              <span className="stat-value">{products.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Currently Showing:</span>
              <span className="stat-value">{filteredProducts.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">In Stock:</span>
              <span className="stat-value">{products.filter(p => p.quantity > 0).length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Categories:</span>
              <span className="stat-value">{categories.length - 1}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;